import type { ReactNode } from "react";

import { META, disponibilidade, resumoBuracos, segmentos } from "../lib/uptime";
import type { Series } from "../types";

interface Props {
  /** Uma entrada por boia selecionada, na ordem em que aparecem nos gráficos. */
  linhas: Array<{ code: string; temperatura: Series | null }>;
}

/**
 * Quanto do intervalo teve dados, por boia.
 *
 * O piloto tem meta de **≥70 % por nó** e até aqui nada a media. Uma barra por
 * boia, um segmento por balde: vê-se logo se o silêncio foi um corte único ou
 * uplink a falhar o tempo todo — coisas diferentes com causas diferentes.
 *
 * Cores de **estado**, não de série: isto não é uma grandeza medida, é presença
 * ou ausência. E a percentagem vem sempre acompanhada da frase, porque um
 * `94 %` sozinho não diz se foram três cortes curtos ou um longo.
 */
export function Disponibilidade({ linhas }: Props): ReactNode {
  return (
    <div className="seccao">
      <h3>Disponibilidade</h3>
      <table className="disponibilidade">
        <caption className="nota">
          Percentagem do intervalo com lotes recebidos. Mede a chegada à API, não a saúde do
          nó: uma boia viva com o 4G em baixo conta como ausente. Meta do piloto: {META} %.
        </caption>
        <thead>
          <tr>
            <th scope="col">Boia</th>
            <th scope="col">Cobertura</th>
            <th scope="col">%</th>
            <th scope="col">Interrupções</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map(({ code, temperatura }) => {
            const d = disponibilidade(temperatura);
            const abaixo = d.percentagem !== null && d.percentagem < META;

            return (
              <tr key={code}>
                <th scope="row">{code}</th>
                <td>
                  <div
                    className="barra"
                    role="img"
                    aria-label={
                      d.percentagem === null
                        ? `${code}: sem dados`
                        : `${code}: ${d.percentagem.toFixed(0)} por cento do intervalo com dados`
                    }
                  >
                    {segmentos(d.baldes).map((s, i) => (
                      <i key={i} className={s} />
                    ))}
                  </div>
                </td>
                <td className={abaixo ? "valor abaixo" : "valor"}>
                  {d.percentagem === null ? "—" : `${d.percentagem.toFixed(0)} %`}
                </td>
                <td className="resumo">{resumoBuracos(d)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
